import { IOperation, ITelemetry } from '@/models/IFunctionality';
import { IRecord, IRecords } from '@/models/IRecord';
import FunctionalityRecordDetail from '../records/FunctionalityRecordDetail';
import { useTranslation } from 'react-i18next';
import {
  interpretDurationPattern,
  translateSchedule
} from '@/utils/functionality-utils';

export interface IFunctionalityTelemetryDetail {
  telemetry: ITelemetry;
  records: IRecords;
  operations: IOperation[];
  'data-testid'?: string;
}

export function FunctionalityTelemetryDetail({
  telemetry,
  records,
  operations,
  'data-testid': dataTestId
}: IFunctionalityTelemetryDetail) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'model.functionality.telemetry'
  });

  const OutputRecordDetail = () => {
    let recordName;
    const outputRecord = () => {
      if (!records) return null;
      const operation = operations.find(
        operation =>
          operation.name === telemetry.operation.$ref.split('/').pop()
      );

      if (!operation || !operation.output) return null;
      recordName = operation.output.$ref.split('/').pop();
      if (!recordName) return null;

      const record = records[recordName];
      if (!record) return null;

      record.doc = operation.doc;
      return record;
    };

    const record = outputRecord();
    if (!record) return <></>;

    return (
      <div data-testid="operation-output-json">
        <FunctionalityRecordDetail
          name={recordName}
          record={record as IRecord}
          records={records}
        />
      </div>
    );
  };

  const Retention = () => {
    const retention = telemetry.parameter_values.find(
      parameter => parameter.name == 'retention'
    );
    if (!retention) return <></>;
    if (Object.keys(retention.value).includes('amount')) {
      return (
        <div
          className="text-base mt-3"
          data-testid="telemetry-retention-amount"
        >
          <span className="font-bold">{t('retention.label')}: </span>
          {t('amount', { amount: retention.value['amount'] })}
        </div>
      );
    }

    if (Object.keys(retention.value).includes('duration')) {
      return (
        <div
          className="text-base mt-3"
          data-testid="telemetry-retention-duration"
        >
          <span className="font-bold">{t('retention.label')}: </span>
          {interpretDurationPattern(retention.value['duration'] as string, t)}
        </div>
      );
    }

    return <>{JSON.stringify(retention.value)}</>;
  };

  const Schedule = () => {
    if (!telemetry.parameter_values) return <></>;
    const schedule = telemetry.parameter_values.find(
      parameter => parameter.name == 'schedule'
    );
    if (!schedule) return <></>;
    return (
      <div className="text-base mt-3">
        <span className="font-bold">{t('schedule.label')}: </span>
        <pre className="text-base font-['ABBvoice'] ml-3">
          {translateSchedule(schedule, t)}
        </pre>
      </div>
    );
  };

  return (
    <div data-testid={dataTestId}>
      <OutputRecordDetail />
      <Retention />
      <Schedule />
    </div>
  );
}
